import {Injectable} from '@angular/core';
import {InitializationAndConfigurationService} from '../configuration/initializationAndConfiguration.service';

/**
 * Evaluates FieldRestrictionDTO objects from the application configuration
 * to determine field visibility and editability based on the logged-in user's
 * permissions, OU responsibilities, and OU tag codes.
 *
 * Mirrors the Java-side FieldRestrictionDTO.canView(User) and canEdit(User) logic.
 */
@Injectable()
export class FieldRestrictionService {

    constructor(private initService: InitializationAndConfigurationService) {
    }

    /**
     * Gets the field restriction map for a given form name from the application configuration.
     * @param formName e.g. "newClientDetails", "existingClientDetails"
     * @returns Map of field name → restriction object, or null if not configured
     */
    getFormRestrictions(formName: string): any {
        const appConfig = this.initService.getApplicationConfiguration();
        if (appConfig?.fieldRestrictionDTOs) {
            return appConfig.fieldRestrictionDTOs[formName] || null;
        }
        return null;
    }

    /**
     * Gets a single field restriction for a given form and field name.
     * @param formName e.g. "newClientDetails"
     * @param fieldName e.g. "clientFirstName", or a ClientIdentifierType code
     * @returns The restriction object, or null if not configured
     */
    getFieldRestriction(formName: string, fieldName: string): any {
        const formRestrictions = this.getFormRestrictions(formName);
        if (formRestrictions) {
            return formRestrictions[fieldName] || null;
        }
        return null;
    }

    /**
     * Determines if the logged-in user can view a field based on its restriction.
     * If no restriction is configured, the field is viewable.
     *
     * Checks (in order):
     * 1. No restriction → viewable
     * 2. Super user/admin → always viewable
     * 3. viewPermissions check
     * 4. viewOuCodes check
     * 5. viewOuTagCodes check
     */
    canView(restriction: any): boolean {
        if (!restriction) {
            return true;
        }

        const user = this.initService.getLoggedInUser();
        if (!user) {
            return false;
        }

        if (user.isSuperUser || user.isSuperAdmin) {
            return true;
        }

        if (!this.hasRequiredPermissions(user, restriction.viewPermissions)) {
            return false;
        }

        if (!this.ouCodesOk(user, restriction.viewOuCodes)) {
            return false;
        }

        return this.ouTagCodesOk(user, restriction.viewOuTagCodes);
    }

    /**
     * Determines if the logged-in user can edit a field based on its restriction.
     * If no restriction is configured, the field is editable.
     *
     * Checks (in order):
     * 1. No restriction → editable
     * 2. editable flag is false → not editable (regardless of user)
     * 3. Super user/admin → always editable
     * 4. editPermissions check
     * 5. editOuCodes check
     * 6. editOuTagCodes check
     */
    canEdit(restriction: any): boolean {
        if (!restriction) {
            return true;
        }

        if (!restriction.editable) {
            return false;
        }

        const user = this.initService.getLoggedInUser();
        if (!user) {
            return false;
        }

        if (user.isSuperUser || user.isSuperAdmin) {
            return true;
        }

        if (!this.hasRequiredPermissions(user, restriction.editPermissions)) {
            return false;
        }

        if (!this.ouCodesOk(user, restriction.editOuCodes)) {
            return false;
        }

        return this.ouTagCodesOk(user, restriction.editOuTagCodes);
    }

    /**
     * Convenience method: checks canView for a specific form + field.
     */
    canViewField(formName: string, fieldName: string): boolean {
        return this.canView(this.getFieldRestriction(formName, fieldName));
    }

    /**
     * Convenience method: checks canEdit for a specific form + field.
     */
    canEditField(formName: string, fieldName: string): boolean {
        return this.canEdit(this.getFieldRestriction(formName, fieldName));
    }

    // ---- Internal helpers ----

    /**
     * Checks if the user has at least one of the required permissions.
     * Handles permission names with or without the "PERM_" prefix,
     * since the frontend user object stores permissions without the prefix.
     */
    private hasRequiredPermissions(user: any, requiredPermissions: string[]): boolean {
        if (!requiredPermissions || requiredPermissions.length === 0) {
            return true;
        }

        if (!user.permissions || user.permissions.length === 0) {
            return false;
        }

        for (const required of requiredPermissions) {
            const normalized = required.startsWith('PERM_') ? required.substring(5) : required;
            if (user.permissions.includes(normalized)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Checks if the user is responsible for at least one of the required OU codes.
     */
    private ouCodesOk(user: any, allowedOuCodes: string[]): boolean {
        if (!allowedOuCodes || allowedOuCodes.length === 0) {
            return true;
        }

        if (!user.ouResponsibilities || user.ouResponsibilities.length === 0) {
            return false;
        }

        const userOuCodes = new Set<string>();
        for (const ou of user.ouResponsibilities) {
            if (ou.code) {
                userOuCodes.add(ou.code);
            }
        }

        for (const code of allowedOuCodes) {
            if (userOuCodes.has(code)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Checks if the user has responsibility for at least one OU
     * that has at least one of the required tag codes.
     */
    private ouTagCodesOk(user: any, allowedOuTagCodes: string[]): boolean {
        if (!allowedOuTagCodes || allowedOuTagCodes.length === 0) {
            return true;
        }

        if (!user.ouResponsibilities || user.ouResponsibilities.length === 0) {
            return false;
        }

        const userTagCodes = new Set<string>();
        for (const ou of user.ouResponsibilities) {
            if (ou.tagsCodes) {
                for (const tagCode of ou.tagsCodes) {
                    userTagCodes.add(tagCode);
                }
            }
        }

        for (const code of allowedOuTagCodes) {
            if (userTagCodes.has(code)) {
                return true;
            }
        }
        return false;
    }
}
